import React, { useCallback, useEffect, useRef } from 'react';
import {
  useFlexLayout, useSortBy, useTable
} from 'react-table'
import AutoSizer from 'react-virtualized-auto-sizer';
import { VariableSizeList } from 'react-window'

import classNames from 'classnames';
import isFunction from 'lodash/isFunction';

import CaretDownIcon from '../icons/CaretDownIcon';
import CaretUpIcon from '../icons/CaretUpIcon';
import Label from '../Labels/Label';
import VerticalGradientOverlay from '../overlays/VerticalGradientOverlay';

const defaultPropGetter = () => ({});

const TableRow = ({ index, style, data }) => {
  const {
    rows,
    prepareRow,
    tableRef,
    rowHeights,
    onItemClick,
    getRowProps,
    getColumnProps,
    getCellProps
  } = data;
  const rowRef = useRef({});
  const row = rows[index]
  prepareRow(row)

  const setRowHeight = useCallback((rowIndex, size) => {
    tableRef?.current?.resetAfterIndex(0);
    rowHeights.current = { ...rowHeights.current, [rowIndex]: size }
  }, [tableRef, rowHeights]);
  
  const hasClickFunction = onItemClick && isFunction(onItemClick);
  const onClick = useCallback(() => {
    if (hasClickFunction) {
      onItemClick(row, index);
    }
  }, [hasClickFunction, row, index, onItemClick])

  useEffect(() => {
    if (rowRef.current) {
      setRowHeight(index, rowRef.current.clientHeight);
    }
  }, [index, setRowHeight]);

  return (
    <div
      ref={rowRef}
      onClick={onClick}
      className={
        classNames(
          'w-full items-center bg-white hover:bg-border-light border-b last:border-b-0 py-8 pl-2',
          { 'cursor-pointer active:bg-border-dark': hasClickFunction }
        )
      }
      {...row.getRowProps({
        ...getRowProps(row),
        style,
      })}
    >
      {row.cells.map(cell => (
        <div
          {...cell.getCellProps([
            {
              className: cell.column.className,
              style: cell.column.style,
            },
            getColumnProps(cell.column),
            getCellProps(cell),
          ])}
        >
          <Label>{cell.render('Cell')}</Label>
        </div>
      ))}
    </div>
  )
}

// Sorting, virtualization, and data driven rendering
export const DataTable = ({
  id,
  className,
  columns, 
  data, 
  defaultColumn,
  onItemClick,
  height = 720,
  estimatedItemSize = 128,
  minItemSize = 128,
  getHeaderProps = defaultPropGetter,
  getColumnProps = defaultPropGetter,
  getRowProps = defaultPropGetter,
  getCellProps = defaultPropGetter,
}) => {
  const tableRef = useRef({});
  const rowHeights = useRef({});
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
  } = useTable(
    {
      columns,
      data,
      defaultColumn,
    },
    useSortBy,
    useFlexLayout,
  )

  const getRowHeight = useCallback((index) => {
    const rowHeight = rowHeights?.current[index]
    if (rowHeight && rowHeight < minItemSize) {
      return minItemSize;
    }
    return rowHeight || estimatedItemSize;
  }, [estimatedItemSize, minItemSize, rowHeights]);

  const tableDisplayProps = { };
  if (className) {
    tableDisplayProps.className = className;
  }

  return (
    <table id={id} className="h-full w-full overflow-x-scroll" {...getTableProps()}>
      {headerGroups.map(headerGroup => (
        <thead className="relative w-full p-2 border-b border-border-dark shadow bg-background" {...headerGroup.getHeaderGroupProps()} style={{ display: 'flex' }}>
          <tr className="w-full flex justify-start">
            {
              headerGroup.headers.map(column => (
                <th
                  className="flex justify-start items-stretch text-brand hover:text-border-darkest active:text-border-dark"
                  {...column.getHeaderProps([
                    {
                      className: column.className,
                      style: column.style,
                    },
                    getColumnProps(column),
                    getHeaderProps(column),
                    column.getSortByToggleProps(),
                  ])}
                >
                  {column.render('Header')}
                  {column.isSorted
                    ? column.isSortedDesc
                      ? <div className="ml-2"><CaretDownIcon /></div>
                      : <div className="ml-2"><CaretUpIcon /></div>
                    : null}
                </th>
              ))
            }
          </tr>
        </thead>
      ))}
      <tbody className="bg-white w-full relative border-b" {...getTableBodyProps()}>
        <VerticalGradientOverlay as="tr" className="top-0 left-0 right-0 mx-0.5" fromDirection="b" />
        <tr>
          <td>
            <AutoSizer disableHeight>
              {
                ({ width }) => (
                  <VariableSizeList
                    ref={tableRef}
                    height={height}
                    itemCount={rows.length}
                    estimatedItemSize={estimatedItemSize}
                    itemSize={getRowHeight}
                    width={width}
                    itemData={{
                      rows,
                      prepareRow,
                      tableRef,
                      rowHeights,
                      onItemClick,
                      getRowProps,
                      getColumnProps,
                      getCellProps
                    }}
                  >
                    {TableRow}
                  </VariableSizeList>
                )
              }
            </AutoSizer>
          </td>
        </tr>
        <VerticalGradientOverlay as="tr" className="bottom-0 left-0 right-0 mx-0.5" fromDirection="t" />
      </tbody>
    </table>
  )
}

export default DataTable;
